import React, { useState } from 'react';
import clsx from 'clsx';
import SVGLocationPinIcon from '@/components/_new/svgs/SVGLocationPinIcon';
import { transformImageUrl } from '@/utils/image.util';
import type { PlaceCategoryDisplay } from '@/utils/placeCategoryDisplay';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';

/**
 * Main photo + up to four tiles: on the right in two rows of two (desktop), or in one row under
 * the main photo (mobile). Rows and tiles stretch to fill.
 */
const GALLERY_RIGHT_TILES = 4;

interface PlaceModalHeroProps {
  title: string;
  category: PlaceCategoryDisplay | null;
  cityLabel: string;
  photoUrls: string[];
  onPhotosClick: () => void;
}

/** Figma Place Page "Hero" below the toolbar: title, category · city, photo gallery (Mobile: "Hotel identity and gallery"). */
const PlaceModalHero: React.FC<PlaceModalHeroProps> = ({
  title,
  category,
  cityLabel,
  photoUrls,
  onPhotosClick,
}) => {
  const isMobile = useIsMobileViewport();
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const photos = photoUrls.filter((url) => !failedUrls.has(url));
  const [mainPhoto, ...restPhotos] = photos;
  const rightPhotos = restPhotos.slice(0, GALLERY_RIGHT_TILES);
  const rightRows = isMobile
    ? [rightPhotos].filter((row) => row.length > 0)
    : [rightPhotos.slice(0, 2), rightPhotos.slice(2, 4)].filter((row) => row.length > 0);
  const hiddenCount = photos.length - 1 - rightPhotos.length;

  const handleError = (url: string) => {
    setFailedUrls((prev) => new Set([...prev, url]));
  };

  const renderTile = (url: string, className: string, overlay?: React.ReactNode) => (
    <button
      key={url}
      type="button"
      onClick={onPhotosClick}
      className={clsx('relative min-w-0 overflow-hidden bg-[#F2F2F2]', className)}
      aria-label="View all photos"
    >
      <img
        src={transformImageUrl(url) || url}
        alt=""
        className="h-full w-full object-cover transition-transform duration-300 hover:scale-[1.03]"
        loading="lazy"
        onError={() => handleError(url)}
      />
      {overlay}
    </button>
  );

  return (
    <div className="flex flex-col gap-2 px-6 pb-0 pt-3 max-md:px-5 max-md:pt-2">
      <h2 className="m-0 font-lora text-[32px] font-semibold leading-[42px] tracking-[0.5px] text-[#252528] max-md:text-[22px] max-md:leading-7 max-md:tracking-[0.02em]">
        {title}
      </h2>

      {(category || cityLabel) && (
        <div className="flex min-w-0 items-center gap-1 font-manrope text-[13px] leading-[18px] text-[#6B6B6B] max-md:gap-[5px] max-md:text-xs max-md:leading-4 max-md:tracking-[0.02em] max-md:text-[#525257]">
          {category && (
            <>
              <span className="flex shrink-0 items-center [&_.MuiSvgIcon-root]:text-[16px] [&_.MuiSvgIcon-root]:text-[#6B6B6B] max-md:[&_.MuiSvgIcon-root]:text-[#252528]" aria-hidden>
                {category.icon}
              </span>
              <span className="shrink-0 capitalize">{category.label}</span>
            </>
          )}
          {category && cityLabel && <span className="max-md:hidden" aria-hidden>·</span>}
          {cityLabel && (
            <>
              <span className="flex shrink-0" aria-hidden>
                <SVGLocationPinIcon size={12} color="#525257" />
              </span>
              <span className="min-w-0 truncate">{cityLabel}</span>
            </>
          )}
        </div>
      )}

      {/* Mobile: 270px main photo over a row of 78px tiles, each rounded on its own (Figma "Photo gallery") */}
      <div className="mt-2 flex h-[320px] gap-1 overflow-hidden rounded-[10px] max-md:h-auto max-md:flex-col max-md:gap-1.5 max-md:rounded-none">
        {mainPhoto ? (
          <>
            {renderTile(
              mainPhoto,
              clsx(
                rightRows.length > 0 ? 'h-full w-1/2 shrink-0' : 'h-full w-full',
                'max-md:h-[270px] max-md:w-full max-md:rounded-xl',
              ),
            )}
            {rightRows.length > 0 && (
              <div className="flex min-w-0 flex-1 flex-col gap-1">
                {rightRows.map((row, rowIndex) => (
                  <div key={rowIndex} className="flex min-h-0 flex-1 gap-1 max-md:h-[78px] max-md:flex-none max-md:gap-1.5">
                    {row.map((url, i) => {
                      const isLastTile = rowIndex === rightRows.length - 1 && i === row.length - 1;
                      return renderTile(
                        url,
                        'h-full flex-1 max-md:rounded-[10px]',
                        isLastTile && hiddenCount > 0 ? (
                          <span className="absolute inset-0 flex items-center justify-center bg-black/40 font-manrope text-sm font-semibold text-white max-md:text-xs">
                            +{hiddenCount} photos
                          </span>
                        ) : undefined,
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-[#F2F2F2] font-manrope text-sm text-[#A7A7AE] max-md:h-[270px] max-md:rounded-xl">
            No photos yet
          </div>
        )}
      </div>
    </div>
  );
};

export default PlaceModalHero;
